import { IHistoryItem } from '@/framework';
import { Framework } from '@/framework/instance';
import { applyDifferentProps, getDifferentProps } from '@/utils/layer-kit';
import { LayerType } from '@/version-types';
import {
    HistoryType,
    IHistoryApplyFilterData,
    IHistoryConvertImageToBackgroundData,
    IHistoryExpendImageAndChangeBgData,
} from './type';

export class ImageHistory extends Framework {
    constructor(identity: string) {
        super(identity);
        this.registerApplyFilter();
        this.registerConvertImageToBackground();
        this.registerExpendImageAndChangeBg();
    }

    /** 转换图片为背景 */
    private registerConvertImageToBackground() {
        this.framework.history.register(HistoryType.CONVERT_IMAGE_TO_BACKGROUND, {
            onRedoUndo: (
                historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isRedo) {
                    this.framework.operate.delLayer(oldLayer.id);
                    this.framework.stage.addOrUpdate(newBgLayer);
                    this.framework.operate.delActiveLayer();
                }
                if (isUndo) {
                    this.framework.stage.addOrUpdate(oldBgLayer);
                    oldLayer.parentId = undefined;
                    this.framework.stage.addOrUpdate(oldLayer);
                    this.framework.operate.setActiveLayer(oldLayer.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (
                historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>,
                isRedo,
                isUndo,
                store,
            ) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isUndo) {
                    historyItem.isDirty =
                        !!this.framework.getLayer(oldLayer.id) || !this.framework.getLayer(oldBgLayer.id);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty =
                            !this.framework.getLayer(oldLayer.id) || !this.framework.getLayer(newBgLayer.id);
                    }
                }
            },
            onMessage: (message: [IHistoryConvertImageToBackgroundData], isRedo, isUndo, isWrite) => {
                const { item } = message[0];
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isRedo || isWrite) {
                    this.framework.operate.tryRemoveActiveLayer(oldLayer.id);
                    this.framework.operate.delLayer(oldLayer.id);
                    this.framework.stage.addOrUpdate(newBgLayer);
                }
                if (isUndo) {
                    this.framework.stage.addOrUpdate(oldBgLayer);
                    oldLayer.parentId = undefined;
                    this.framework.stage.addOrUpdate(oldLayer);
                }
            },
        });
    }

    /** 应用滤镜 */
    private registerApplyFilter() {
        this.framework.history.register(HistoryType.APPLY_FILTER, {
            isLikeDefault: true,
            onRedoUndo: (historyItem: IHistoryItem<IHistoryApplyFilterData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { item } = historyItem.data.data;
                const { oldLayer, newLayer, diffInfo } = item;
                if (diffInfo) {
                    Object.keys(diffInfo).forEach((layerId) => {
                        const differentProps = diffInfo[layerId];
                        const layer = this.framework.getLayer(layerId, true);
                        if (!layer) return;
                        applyDifferentProps(layer, differentProps);
                        this.framework.stage.addOrUpdate(layer);
                    });
                } else {
                    if (isRedo) {
                        this.framework.stage.addOrUpdate(newLayer);
                    } else {
                        this.framework.stage.addOrUpdate(oldLayer);
                    }
                }
                this.framework.operate.setActiveLayer(oldLayer.id);
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryApplyFilterData>, isRedo, isUndo, store) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newLayer } = item;
                if (isUndo) {
                    const latestLayer = this.framework.getLayer(oldLayer.id, true);
                    if (!latestLayer) {
                        historyItem.isDirty = true;
                    }
                    historyItem.data.data.item.diffInfo = getDifferentProps(
                        oldLayer,
                        newLayer,
                        undefined,
                        new Set(['parentId']),
                    );
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                        oldLayer,
                        newLayer: latestLayer,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const { oldLayer, newLayer } = beforeData;
                        const latestLayer = this.framework.getLayer(newLayer.id);
                        if (!latestLayer) {
                            historyItem.isDirty = true;
                            return;
                        }
                        historyItem.data.data.item.diffInfo = getDifferentProps(
                            newLayer,
                            oldLayer,
                            undefined,
                            new Set(['parentId']),
                        );
                        const data = historyItem.data.data;
                        data.item.newLayer = newLayer;
                        data.item.oldLayer = oldLayer;
                    }
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryApplyFilterData>) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newLayer } = item;
                return {
                    message: [getDifferentProps(newLayer, oldLayer), oldLayer.id],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryApplyFilterData>) => {
                const { item } = historyItem.data.data;
                return {
                    message: [item.diffInfo, item.oldLayer.id],
                };
            },
            onMessage: (message: [Record<string, object>, string]) => {
                const [diffInfo, layerId] = message;
                if (!diffInfo) return;
                const layer = this.framework.getLayer(layerId, true);
                if (!layer) return;
                Object.keys(diffInfo).forEach((layerId) => {
                    const differentProps = diffInfo[layerId];
                    const layer = this.framework.getLayer(layerId, true);
                    if (!layer) return;
                    applyDifferentProps(layer, differentProps);
                    this.framework.stage.addOrUpdate(layer);
                    this.framework.operate.tryRefreshActiveLayer(layerId);
                });
            },
        });
    }

    /**
     * 扩展图片并更换背景
     *
     * 1.从官网带图片进入编辑器，改尺寸后会同步修改背景尺寸
     * 示例：
     * http://localhost:3003/design/?createEmptyPoster=true&modelId=0&unit=px&width=600&height=600&open=https%3A%2F%2Fxiuxiu-pro-pre.meitudata.com%2Fposters%2Fcefc32facab3523618ae44cd9ccd7088.png&feature=image_expand
     *
     * 2.普通扩图功能历史记录写入的是默认格式，调用的是
     * imageLayer.processAIExpendImage 处理历史记录
     */
    private registerExpendImageAndChangeBg() {
        this.framework.history.register(HistoryType.EXPEND_IMAGE_AND_CHANGE_BG, {
            onRedoUndo: (
                historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer, newLayer } = item;
                if (isRedo) {
                    this.framework.stage.addOrUpdate(newLayer);
                    this.framework.stage.addOrUpdate(newBgLayer);
                    if (newLayer.layerType !== LayerType.MAIN_LAYER) {
                        this.framework.operate.setActiveLayer(newLayer.id);
                    }
                }
                if (isUndo) {
                    this.framework.stage.addOrUpdate(oldLayer);
                    this.framework.stage.addOrUpdate(oldBgLayer);
                    if (oldLayer.layerType !== LayerType.MAIN_LAYER) {
                        this.framework.operate.setActiveLayer(oldLayer.id);
                    }
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>) => {
                this.framework.operate.tryRefreshActiveLayer(historyItem.data.data.item.newLayer.id);
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (
                historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>,
                isRedo,
                isUndo,
                store,
            ) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer, newLayer } = item;
                if (isUndo) {
                    historyItem.isDirty =
                        this.framework.checkLayerIsDirty({
                            originLayer: newLayer,
                        }) ||
                        this.framework.checkLayerIsDirty({
                            originLayer: newBgLayer,
                        });
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty =
                            this.framework.checkLayerIsDirty({
                                originLayer: oldLayer,
                            }) ||
                            this.framework.checkLayerIsDirty({
                                originLayer: oldBgLayer,
                            });
                    }
                }
            },
            onMessage: (
                message: [IHistoryExpendImageAndChangeBgData],
                isRedo: boolean,
                isUndo: boolean,
                isWrite: boolean,
            ) => {
                const { item } = message[0];
                const { oldLayer, newBgLayer, oldBgLayer, newLayer } = item;
                if (isRedo || isWrite) {
                    newLayer.parentId = this.framework.getLayer(newLayer.id)?.parentId;
                    this.framework.stage.addOrUpdate(newLayer);
                    this.framework.stage.addOrUpdate(newBgLayer);
                    if (newLayer.layerType !== LayerType.MAIN_LAYER) {
                        this.framework.operate.tryRefreshActiveLayer(newLayer.id);
                    }
                }
                if (isUndo) {
                    oldLayer.parentId = this.framework.getLayer(oldLayer.id)?.parentId;
                    this.framework.stage.addOrUpdate(oldLayer);
                    this.framework.stage.addOrUpdate(oldBgLayer);
                    if (oldLayer.layerType !== LayerType.MAIN_LAYER) {
                        this.framework.operate.tryRefreshActiveLayer(oldLayer.id);
                    }
                }
            },
        });
    }
}
